import React, { useState } from 'react';

interface ApiRequestBlockProps {
    method: string;
    baseUrl: string;
    path: string;
}

const methodColors: Record<string, string> = {
    GET: '#61AFFE',
    POST: '#49CC90',
    PUT: '#FCA130',
    PATCH: '#50E3C2',
    DELETE: '#F93E3E',
};

const copyToClipboard = async (text: string) => {
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
        } else {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
        return true;
    } catch (err) {
        console.error('Copy failed:', err);
        return false;
    }
};

export const ApiRequestBlock: React.FC<ApiRequestBlockProps> = ({
    method,
    baseUrl,
    path,
}) => {
    const [copied, setCopied] = useState(false);
    const fullUrl = `${baseUrl}${path}`;

    const handleCopyUrl = async () => {
        const success = await copyToClipboard(`${method} ${fullUrl}`);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: '8px 12px',
                background: '#fafafa',
                fontFamily: 'monospace',
                gap: 10,
            }}
        >
            {/* Method pill */}
            <span
                style={{
                    color: methodColors[method],
                    backgroundColor: `${methodColors[method]}1A`,
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontWeight: 600,
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                }}
            >
                {method}
            </span>

            {/* Full URL */}
            <span style={{ flex: 1, overflowWrap: 'anywhere' }}>
                {fullUrl}
            </span>

            {/* Copy button */}
            <button
                onClick={handleCopyUrl}
                title={copied ? 'Copied' : 'Copy'}
                style={{
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: copied ? '#16a34a' : '#2563eb',
                    fontSize: 14,
                }}
            >
                {copied ? '✓' : '📋'}
            </button>
        </div>
    );
};

const postmanJsonHighlight = (json: object) => {
    const jsonString = JSON.stringify(json, null, 2)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    return jsonString.replace(
        /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|\b\d+\b)/g,
        (match) => {
            let color = '#111';

            if (/^".*":$/.test(match)) color = '#C92C2C';       // keys
            else if (/^"/.test(match)) color = '#1A73E8';      // string values
            else if (/true|false/.test(match)) color = '#E65100';
            else if (/null/.test(match)) color = '#6B7280';
            else color = '#8E24AA';                             // numbers

            return `<span style="color:${color}">${match}</span>`;
        }
    );
};


interface JsonBlockProps {
    data: object;
    copyable?: boolean;
}

export const JsonBlock: React.FC<JsonBlockProps> = ({
    data,
    copyable = false,
}) => {
    const [copied, setCopied] = useState(false);
    const handleCopyJSON = async () => {
        const success = await copyToClipboard(
            JSON.stringify(data, null, 2)
        );
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }
    };

    return (
        <div
            style={{
                position: 'relative',
                marginTop: 8,
            }}
        >
            {/* Copy button */}
            {copyable && (
                <button
                    onClick={handleCopyJSON}
                    title={copied ? 'Copied' : 'Copy'}
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 14,
                        color: copied ? '#16a34a' : '#2563eb',
                    }}
                >
                    {copied ? '✓' : '📋'}
                </button>
            )}

            {/* JSON */}
            <pre
                style={{
                    background: '#FAFAFA',
                    border: '1px solid #D9D9D9',
                    borderRadius: 6,
                    padding: '16px 12px',
                    fontFamily: 'Menlo, Monaco, Consolas, monospace',
                    fontSize: 13,
                    overflowX: 'auto',
                }}
            >
                <code
                    dangerouslySetInnerHTML={{
                        __html: postmanJsonHighlight(data),
                    }}
                />
            </pre>
        </div>
    );
};


export const AuthHeaderBlock = () => (
    <pre className="code-block">
        <code>Authorization: Bearer {"<access_token>"}</code>
    </pre>
);

interface ApiField {
    name: string;
    type: string;
    required: boolean;
    description: string;
}


interface FieldTableProps {
    fields: ApiField[];
}

const thStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '1px solid #e5e7eb',
    background: '#f9fafb',
    fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: '1px solid #e5e7eb',
    verticalAlign: 'top',
};


export const FieldTable: React.FC<FieldTableProps> = ({ fields }) => {
    return (
        <div style={{ overflowX: 'auto' }}>
            <table
                style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginTop: 8,
                    fontSize: 14,
                }}
            >
                <thead>
                    <tr>
                        <th style={thStyle}>Field</th>
                        <th style={thStyle}>Type</th>
                        <th style={thStyle}>Required</th>
                        <th style={thStyle}>Description</th>
                    </tr>
                </thead>

                <tbody>
                    {fields.map((f) => (
                        <tr key={f.name}>
                            <td style={tdStyle}>
                                <code>{f.name}</code>
                            </td>
                            <td style={tdStyle}>{f.type}</td>
                            <td style={tdStyle}>
                                {f.required ? (
                                    <span style={{ color: '#16a34a', fontWeight: 500 }}>
                                        Yes
                                    </span>
                                ) : (
                                    <span style={{ color: '#ff4400' }}>No</span>
                                )}
                            </td>
                            <td style={tdStyle}>{f.description}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
